import * as React from 'react';
import { useSelector } from 'react-redux';
import { Box, Paper, Typography, TextField, MenuItem, Button } from '@material-ui/core';
import type { WehaLine, WehaGlobalSettings } from '../../Reducers/Configurator/types';
import { useTheme } from '@material-ui/core/styles';
import CustomLabel from './CustomLabel';
import ChurchIcon from '../Icons/ChurchIcon';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import {
  getSizesForFont,
  getSpecialCharactersForFont,
  getContentType,
  filterSizesByContentType,
} from '../../Reducers/Configurator/fontData';
import { selectWehaFontData } from '../../Reducers/Configurator/Selectors';
import { CASING_GROSS, CASING_GEMISCHT } from '../../constants/casing';
import { isDefaultLinePlaceholderText } from '../../constants/lineText';
import { isVerticalTextAvailable } from '../../Reducers/Configurator/Selectors';

interface SelectedLineSettingsProps {
  line: WehaLine | null;
  globalSettings: WehaGlobalSettings;
  onUpdateLine: (lineId: string | number, key: string, value: unknown) => void;
  onCasingChange?: (lineId: string | number, newValue: string) => void;
  /** Called when user clicks special-chars button; pass cursor index for insertion. */
  onOpenSpecialCharsModal?: (cursorPosition: number) => void;
  onRemoveLineIfEmpty?: (lineId: string | number) => void;
  /** When true (e.g. special-chars modal open), skip removing empty line on blur so the line stays while dialog is open. */
  specialCharsModalOpenRef?: React.MutableRefObject<boolean>;
  /** Called on mousedown of the special-chars button so parent can set the ref before blur runs (prevents empty line being removed). */
  onSpecialCharsButtonMouseDown?: () => void;
}

const IS_GROSS = (s: string | undefined) => (s ?? '') === CASING_GROSS;

/** ASCII letters plus German umlauts/Eszett (ÄÖÜ so keyboards / GROSS casing match). */
const ASCII_AND_GERMAN_LETTER = /[a-zA-ZäöüßÄÖÜ]/;

export default function SelectedLineSettings({
  line,
  globalSettings,
  onUpdateLine,
  onCasingChange,
  onOpenSpecialCharsModal,
  onRemoveLineIfEmpty,
  specialCharsModalOpenRef,
  onSpecialCharsButtonMouseDown,
}: SelectedLineSettingsProps) {
  const theme = useTheme();
  const w = (theme as { weha?: Record<string, unknown> }).weha || {};
  const fontData = useSelector(selectWehaFontData);
  const allSizes = getSizesForFont(fontData, globalSettings.fontFamily ?? '');
  const specialCharsString = getSpecialCharactersForFont(fontData, globalSettings.fontFamily ?? '');
  const allowedSpecialSet = React.useMemo(
    () => new Set((specialCharsString || '').split('')),
    [specialCharsString]
  );
  const contentType = getContentType(line?.text ?? '');
  const availableSizes = filterSizesByContentType(allSizes, contentType);
  const currentSizeInList = line ? availableSizes.some((s) => s.value === line.fontSize) : false;
  const firstAvailableSize = availableSizes[0]?.value;
  const textInputRef = React.useRef<HTMLInputElement | null>(null);
  const autoSelectedPlaceholderLineIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (line == null || availableSizes.length === 0 || currentSizeInList || !firstAvailableSize) return;
    onUpdateLine(line.id, 'fontSize', firstAvailableSize);
  }, [line?.id, availableSizes.length, currentSizeInList, firstAvailableSize, onUpdateLine]);

  const selectAllPlaceholderInInput = React.useCallback((text: string) => {
    const input = textInputRef.current;
    if (!input || !isDefaultLinePlaceholderText(text)) return;
    input.focus({ preventScroll: true });
    const len = input.value.length;
    if (len > 0) {
      input.setSelectionRange(0, len);
    }
  }, []);

  const applyPlaceholderAutofocus = React.useCallback(
    (lineId: string, text: string) => {
      if (autoSelectedPlaceholderLineIdRef.current === lineId) {
        return document.activeElement === textInputRef.current;
      }
      autoSelectedPlaceholderLineIdRef.current = lineId;
      selectAllPlaceholderInInput(text);
      return document.activeElement === textInputRef.current;
    },
    [selectAllPlaceholderInInput]
  );

  const schedulePlaceholderAutofocus = React.useCallback(
    (lineId: string, text: string, attemptsLeft = 8) => {
      const run = (left: number) => {
        if (applyPlaceholderAutofocus(lineId, text) || left <= 0) return;
        requestAnimationFrame(() => run(left - 1));
      };
      requestAnimationFrame(() => run(attemptsLeft));
    },
    [applyPlaceholderAutofocus]
  );

  const bindTextInputRef = React.useCallback(
    (el: HTMLInputElement | null) => {
      (textInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
      if (el == null || line == null || !isDefaultLinePlaceholderText(line.text)) return;
      schedulePlaceholderAutofocus(String(line.id), line.text ?? '');
    },
    [line, schedulePlaceholderAutofocus]
  );

  React.useLayoutEffect(() => {
    if (line == null || !isDefaultLinePlaceholderText(line.text)) return;
    schedulePlaceholderAutofocus(String(line.id), line.text ?? '');
  }, [line?.id, line?.text, schedulePlaceholderAutofocus]);

  if (line == null) return null;

  const isPlaceholderContent = isDefaultLinePlaceholderText(line.text);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  const handleTextChange = (value: string) => {
    // Placeholder is letters-only; use incoming value to pick numbers vs letters, not locked type.
    const ct = isDefaultLinePlaceholderText(line.text) ? null : getContentType(line.text);
    const allow = (c: string) =>
      ASCII_AND_GERMAN_LETTER.test(c) || /\d/.test(c) || c === ' ' || allowedSpecialSet.has(c);
    let next: string;
    if (ct === 'numbers') {
      next = value
        .split('')
        .filter((c) => /\d/.test(c) || c === ' ' || c === '.' || allowedSpecialSet.has(c))
        .join('');
    } else if (ct === 'letters') {
      next = value
        .split('')
        .filter((c) => ASCII_AND_GERMAN_LETTER.test(c) || c === ' ' || c === '.' || allowedSpecialSet.has(c))
        .join('');
    } else if (value.length > 0) {
      const hasDigit = /\d/.test(value);
      const hasLetter = ASCII_AND_GERMAN_LETTER.test(value);
      if (hasDigit && !hasLetter) {
        next = value
          .split('')
          .filter((c) => /\d/.test(c) || c === ' ' || c === '.' || allowedSpecialSet.has(c))
          .join('');
      } else if (hasLetter) {
        next = value
          .split('')
          .filter(
            (c) => ASCII_AND_GERMAN_LETTER.test(c) || c === ' ' || c === '.' || allowedSpecialSet.has(c)
          )
          .join('');
      } else {
        next = value.split('').filter(allow).join('');
      }
    } else {
      next = value;
    }
    onUpdateLine(line.id, 'text', IS_GROSS(line.casing) ? next.toUpperCase() : next);
  };

  const handleContentFocus = () => {
    if (!isPlaceholderContent) return;
    selectAllPlaceholderInInput(line.text ?? '');
  };

  const handleContentBlur = () => {
    if (!onRemoveLineIfEmpty || (line.text || '').trim() !== '') return;
    const lineId = line.id;
    setTimeout(() => {
      if (specialCharsModalOpenRef?.current) return;
      onRemoveLineIfEmpty(lineId);
    }, 0);
  };

  const handleOpenSpecialCharsClick = () => {
    const input = textInputRef.current;
    const pos =
      input != null && typeof input.selectionStart === 'number'
        ? input.selectionStart
        : (line.text || '').length;
    onOpenSpecialCharsModal?.(pos);
  };

  const handleCasingChange = (newValue: string) => {
    if (onCasingChange) {
      onCasingChange(line.id, newValue);
    } else {
      onUpdateLine(line.id, 'casing', newValue);
    }
  };

  const handleMouseDown = () => {
    if (isPlaceholderContent) {
      onUpdateLine(line.id, 'text', '');
    }
  };

  return (
    <Paper
      elevation={0}
      style={{
        backgroundColor: w.paperBackground || theme.palette.background.paper,
        padding: 24,
        border: `1px solid ${w.borderLight || theme.palette.divider}`,
        borderRadius: w.borderRadius ?? theme.shape.borderRadius,
        boxShadow: w.shadowPaper || theme.shadows[1],
      }}
    >
      <Typography
        variant="subtitle2"
        style={{
          fontWeight: 700,
          marginBottom: 20,
          color: w.textPrimary || theme.palette.text.primary,
          display: 'flex',
          alignItems: 'center',
          fontSize: '0.95rem',
        }}
      >
        <span
          className="icon-check"
          style={{ marginRight: 12, color: w.primary || theme.palette.primary.main, fontSize: '1.2rem' }}
        />
        {t('weha.selectedLine.title')}
      </Typography>

      <Box mb={2.5}>
        <CustomLabel>{t('weha.selectedLine.contentLabel')}</CustomLabel>
        <Box display="flex">
          <TextField
            inputRef={bindTextInputRef}
            autoFocus={isPlaceholderContent}
            fullWidth
            variant="outlined"
            size="small"
            value={line.text ?? ''}
            onChange={(e) => handleTextChange(e.target.value)}
            onFocus={handleContentFocus}
            onBlur={handleContentBlur}
            onKeyDown={handleKeyDown}
            onMouseDown={handleMouseDown}
            inputProps={{ maxLength: 256 }}
            InputProps={{ style: { backgroundColor: w.inputBackground || theme.palette.background.paper } }}
          />
          <Button
            variant="outlined"
            onMouseDownCapture={onSpecialCharsButtonMouseDown}
            onClick={handleOpenSpecialCharsClick}
            disabled={specialCharsString.length === 0}
            style={{
              marginLeft: 12,
              minWidth: 48,
              padding: 0,
              borderColor: w.borderDefault || theme.palette.divider,
              backgroundColor: w.paperBackground || theme.palette.background.paper,
              borderRadius: w.borderRadius ?? theme.shape.borderRadius,
            }}
          >
            <ChurchIcon sx={{ fontSize: '1.2rem', color: w.textSecondary || theme.palette.text.secondary }} />
          </Button>
        </Box>
      </Box>

      {availableSizes.length > 0 && (
        <Box mb={2.5}>
          <CustomLabel>{t('weha.selectedLine.fontSizeLabel')}</CustomLabel>
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            value={currentSizeInList ? line.fontSize : (availableSizes[0]?.value ?? '')}
            onChange={(e) => onUpdateLine(line.id, 'fontSize', e.target.value)}
            InputProps={{ style: { backgroundColor: w.inputBackground || theme.palette.background.paper } }}
          >
            {availableSizes.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.value} mm
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      <Box mb={2.5}>
        <CustomLabel>{t('weha.selectedLine.caseLabel')}</CustomLabel>
        <TextField
          select
          fullWidth
          variant="outlined"
          size="small"
          value={line.casing ?? CASING_GEMISCHT}
          onChange={(e) => handleCasingChange(e.target.value)}
          InputProps={{ style: { backgroundColor: w.inputBackground || theme.palette.background.paper } }}
        >
          <MenuItem value={CASING_GEMISCHT}>{t('weha.selectedLine.caseMixed')}</MenuItem>
          <MenuItem value={CASING_GROSS}>{t('weha.selectedLine.caseUpper')}</MenuItem>
        </TextField>
      </Box>

      <Box mt={2.5}>
        <CustomLabel>{t('weha.selectedLine.directionLabel')}</CustomLabel>
        <TextField
          select
          fullWidth
          variant="outlined"
          size="small"
          value={line.vertical ? 'vertical' : 'horizontal'}
          onChange={(e) => onUpdateLine(line.id, 'vertical', e.target.value === 'vertical')}
          InputProps={{ style: { backgroundColor: w.inputBackground || theme.palette.background.paper } }}
        >
          <MenuItem value="horizontal">{t('weha.selectedLine.directionHorizontal')}</MenuItem>
          {isVerticalTextAvailable(globalSettings) && (
            <MenuItem value="vertical">{t('weha.selectedLine.directionVertical')}</MenuItem>
          )}
        </TextField>
      </Box>
    </Paper>
  );
}
